import { create } from 'mutative';

import { type DeliveryNote } from '~/types/deliveryNote';

import { type FormDeliveryNoteFormFields } from '~/modules/ai-parser-review/components/FormDeliveryNote';
import { guaranteeDeliveryNoteStructure } from '~/modules/delivery-notes/utils/guaranteeDeliveryNoteStructure';

import { removeUndefinedKeys } from '~/utils/api';

import { replaceEmptyStringsWithOriginalUndefined } from '../replaceEmptyStringsWithOriginalUndefined';

import { updateDeliveryData } from './updateDeliveryData';
import { updateExecutionData } from './updateExecutionData';
import { updateHeaderData } from './updateHeaderData';
import { updateLineItemsData } from './updateLineItemsData';
import { updatePartiesData } from './updatePartiesData';

/**
 * Maps the form data to the format required by the API.
 *
 * @param formData - The form data to map.
 * @param originalAssetBody - The original asset body to update.
 * @returns The mapped data.
 */
export const mapUpdateInputDataMutationData = (
  formData: FormDeliveryNoteFormFields,
  originalAssetBody: DeliveryNote,
) => {
  /*
   * First merge the originalAssetBody into the full structure of a delivery note.
   * This prevents us from having to deal with nested properties that don't exist yet.
   * Then update the nested properties based on the formData.
   * Finally, remove all undefined values with the removeUndefinedKeys function
   * before submitting the data to the API,
   */
  const originalData = guaranteeDeliveryNoteStructure(originalAssetBody);

  const body = create(originalData, (draft) => {
    updateHeaderData(draft, formData);

    updatePartiesData(draft, formData);

    updateDeliveryData(draft, formData);
    updateExecutionData(draft, formData);

    updateLineItemsData(draft, formData);
  });

  const processedBody = replaceEmptyStringsWithOriginalUndefined(
    body,
    originalAssetBody,
  );

  return removeUndefinedKeys(processedBody);
};
